<template>
  <div v-if="tableData" class="tokeva-table-container my-3">
    <div class="tokeva-table-scroll-wrapper elevation-1 rounded">
      <table class="tokeva-plain-table">
        <thead v-if="headers.length > 0">
          <tr>
            <th
              v-for="(headerCell, hIdx) in headers"
              :key="'th-' + hIdx"
              :colspan="headerCell.colspan || 1"
              :rowspan="headerCell.rowspan || 1"
              class="tokeva-th"
            >
              <div v-for="(line, lIdx) in headerCell.lines" :key="'th-line-' + lIdx">
                <tokeva-rich-text
                  :content="line"
                  @open-gas-guide="$emit('open-gas-guide')"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rIdx) in dataRows"
            :key="'tr-' + rIdx"
            :class="rIdx % 2 === 0 ? 'tr-even' : 'tr-odd'"
          >
            <td
              v-for="(cell, cIdx) in row"
              :key="'td-' + cIdx"
              :colspan="cell.colspan || 1"
              :rowspan="cell.rowspan || 1"
              :class="[
                'tokeva-td',
                { 'font-weight-bold': cell.isBold },
                isUnColumn(cIdx) ? 'tokeva-td-un' : '',
              ]"
            >
              <!-- Special clickable UN numbers if this is the YK-numero column -->
              <div v-if="isUnColumn(cIdx) && extractUnTokens(cell.text).length > 0" class="d-flex flex-wrap gap-1">
                <v-chip
                  v-for="un in extractUnTokens(cell.text)"
                  :key="un"
                  x-small
                  color="orange darken-4"
                  dark
                  class="font-weight-bold cursor-pointer un-chip mr-1 mb-1"
                  title="Valitse tämä aine haussa"
                  @click="$emit('select-un', un)"
                >
                  YK {{ un }}
                </v-chip>
              </div>

              <!-- Regular cell lines -->
              <div v-else>
                <div
                  v-for="(line, lIdx) in cell.lines"
                  :key="'line-' + lIdx"
                  :class="{ 'mb-1': lIdx < cell.lines.length - 1 }"
                >
                  <tokeva-rich-text
                    :content="line"
                    @open-gas-guide="$emit('open-gas-guide')"
                  />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import TokevaRichText from "@/components/TokevaRichText.vue";

let tablesData = null;

export default {
  name: "TokevaTable",
  components: {
    TokevaRichText,
  },
  props: {
    tableId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    tables: {},
  }),
  computed: {
    tableData() {
      if (!this.tableId || !this.tables) return null;
      return this.tables[this.tableId] || null;
    },
    headers() {
      return this.tableData?.rows?.[0] || [];
    },
    dataRows() {
      return this.tableData?.rows?.slice(1) || [];
    },
  },
  async created() {
    await this.loadTablesData();
  },
  methods: {
    async loadTablesData() {
      if (!tablesData) {
        const res = await import("@/data/tokeva/tables.json");
        tablesData = res.default || res;
      }
      this.tables = tablesData;
    },
    isUnColumn(colIdx) {
      if (!this.headers || this.headers.length <= colIdx) return false;
      const headerText = (this.headers[colIdx]?.text || "").toLowerCase();
      return headerText.includes("yk-numero") || headerText.includes("yk/un") || headerText.includes("yk nro");
    },
    extractUnTokens(text) {
      if (!text) return [];
      const matches = text.match(/\b\d{4}\b/g);
      return matches ? Array.from(new Set(matches)) : [];
    },
  },
};
</script>

<style scoped lang="scss">
.tokeva-table-container {
  width: 100%;
}

.tokeva-table-scroll-wrapper {
  overflow-x: auto;
  max-width: 100%;
  background-color: #ffffff;
  border: 1px solid #777777;
}

.tokeva-plain-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.875rem;

  .tokeva-th {
    background-color: #f1f3f4;
    border: 1px solid #777777;
    padding: 8px 12px;
    font-weight: 700;
    color: #1a1a1a;
    font-size: 0.85rem;
    line-height: 1.35;
    vertical-align: middle;
  }

  .tokeva-td {
    border: 1px solid #888888;
    padding: 7px 12px;
    font-size: 0.85rem;
    line-height: 1.4;
    vertical-align: top;
    color: #222222;
  }

  .tr-even {
    background-color: #ffffff;
  }

  .tr-odd {
    background-color: #f9fafb;
  }

  tr:hover {
    background-color: #f1f5f9;
  }
}

.un-chip {
  transition: transform 0.15s ease;
  &:hover {
    transform: scale(1.05);
  }
}

.cursor-pointer {
  cursor: pointer;
}

.gap-1 {
  gap: 4px;
}

.theme--dark {
  .tokeva-table-scroll-wrapper {
    background-color: #1e1e1e;
    border-color: #555555;
  }

  .tokeva-plain-table {
    .tokeva-th {
      background-color: #2d2d2d;
      border-color: #555555;
      color: #ffffff;
    }

    .tokeva-td {
      border-color: #444444;
      color: #e0e0e0;
    }

    .tr-even {
      background-color: #1e1e1e;
    }

    .tr-odd {
      background-color: #252525;
    }

    tr:hover {
      background-color: #333333;
    }
  }
}
</style>

